import React from 'react'
import styled from 'styled-components'

import { evaluationGet } from '../requests/evaluation'
import { getFullName } from '../utils/getFullName'
import { PATHS } from '../constants/paths'
import { dateToDateTime, dateToDDMMYYYY } from '../utils/dateToDDMMYYYY'
import {
  VARIANT_DANGER, VARIANT_DISABLED, VARIANT_SECONDARY, VARIANT_SUCCESS, VARIANT_WARNING,
} from '../constants/variants'
import { GRID_BLOCK_TYPE } from '../constants/gridBlockType'
import { theme } from '../constants/theme'

import { PageTitle } from './common/PageTitle'
import {
  H3, Label, Body,
} from './common/Typography'
import { ItemLoader } from './ItemLoader'
import { Trad } from './common/Trad'
import { Margin } from './common/Margin'
import { Block } from './common/Block'


export const EvaluationShow = ItemLoader({
  request: evaluationGet,
  pageTitle: ({ item }) => (
    <PageTitle
      backLink={PATHS.EVALUATION_LIST}
      backLinkTrad="evaluation"
      title={getFullName({ user: item.trainee })}
    />
  ),
})(({ getRequest }) => {
  const evaluation = getRequest.state.data

  return (
    <>
      <Block border>
        <div>
          <Trad id="evaluationBy" />
          {' '}
          {evaluation.instructor ? getFullName({ user: evaluation.instructor }) : '-'}
        </div>
        <div>
          <Trad id="date" />
          {': '}
          {dateToDateTime(evaluation.creationDate)}
        </div>
      </Block>
      <div>
        <Margin v={30} />
        <H3 tradId="internshipInfos" bold variant={VARIANT_SECONDARY} />
        <Margin v={15} />
        <Block border>
          <InnerWrapper>
            <Body tradId="internshipName" variant={VARIANT_SECONDARY} />
            <Margin v={5} />
            {evaluation.internship.name}
            <Margin v={15} />
            <Body tradId="dates" variant={VARIANT_SECONDARY} />
            <Margin v={5} />
            {dateToDDMMYYYY(evaluation.internship.startDate)}
            {' - '}
            {dateToDDMMYYYY(evaluation.internship.endDate)}
          </InnerWrapper>
        </Block>
        <Margin v={30} />
        <H3 bold variant={VARIANT_SECONDARY}>
          <Trad id="eval" />
          {'  '}
          {evaluation.activity}
        </H3>
        <Margin v={15} />
        <InnerWrapper>
          <Block border>
            {evaluation.quotation && (
              <div>
                <Body tradId="quotation" variant={VARIANT_SECONDARY} />
                <Margin v={5} />
                {evaluation.quotation}
                <Margin v={10} />
              </div>
            )}
            {evaluation.grades.map((grade, index) => (
              <div key={index}>
                <Body variant={VARIANT_SECONDARY}>{`${grade.text} : `}</Body>
                {grade.value}
                <Margin v={10} />
              </div>
            ))}
          </Block>
          <Margin v={15} />
          {evaluation.blocks.map((block) => {
            const { type } = block
            let items = null
            if (type === GRID_BLOCK_TYPE.colored) {
              items = block.criterions.map((criterion, index) => (
                <div key={index}>
                  <Body>{criterion.text}</Body>
                  <Margin v={10} />
                  <Margin h={10} />
                  <Circle color={theme.colors.green} filled={criterion.value === 3} />
                  <Margin h={35} />
                  <Circle color={theme.colors.warningYellow} filled={criterion.value === 2} />
                  <Margin h={35} />
                  <Circle color={theme.colors.red} filled={criterion.value === 1} />
                  <Margin h={35} />
                  <Circle color={theme.colors.grayDark} filled={criterion.value === 0} />
                  <Margin v={20} />
                </div>
              ))
            }
            if (type === GRID_BLOCK_TYPE.comment) {
              items = block.comments.map((comment, index) => (
                <div key={index}>
                  <Body>{comment.name}</Body>
                  <Margin v={10} />
                  <Block border>
                    {comment.value}
                  </Block>
                  <Margin v={10} />
                </div>
              ))
            }
            if (type === GRID_BLOCK_TYPE.custom) {
              items = block.customCriterions.map((criterion, index) => (
                <div key={index}>
                  <Body>{criterion.name}</Body>
                  <Margin v={10} />
                  <CustomCriterion selected={criterion.value === 0}>{criterion.value0}</CustomCriterion>
                  <Margin v={10} />
                  <CustomCriterion selected={criterion.value === 1}>{criterion.value1}</CustomCriterion>
                  <Margin v={10} />
                  <CustomCriterion selected={criterion.value === 2}>{criterion.value2}</CustomCriterion>
                  <Margin v={20} />
                </div>
              ))
            }

            return (
              <div key={block.name}>
                <Labels>
                  <Label variant={VARIANT_SECONDARY} bold>{block.name}</Label>
                  {block.type === GRID_BLOCK_TYPE.colored && (
                    <div>
                      <Body variant={VARIANT_SUCCESS} tradId="good" />
                      <Margin h={10} />
                      <Body variant={VARIANT_WARNING} tradId="average" />
                      <Margin h={10} />
                      <Body variant={VARIANT_DANGER} tradId="insufficient" />
                      <Margin h={10} />
                      <Body variant={VARIANT_DISABLED} tradId="notRated" />
                    </div>
                  )}
                </Labels>
                <Margin v={10} />
                <Block border>
                  {items}
                </Block>
                <Margin v={10} />
              </div>
            )
          })}
        </InnerWrapper>
      </div>
    </>
  )
})

const InnerWrapper = styled.div`
  padding: 10px;
`

const Circle = styled.span`
  display: inline-block;
  height: 35px;
  width: 35px;
  border-radius: 50%;
  background: ${({ color, filled }) => (filled ? color : 'none')};
  border: 1px solid ${({ color }) => color};
`

const CustomCriterion = styled.div`
  padding: 10px 16px;
  color: ${({ selected }) => (selected ? theme.colors.white : theme.colors.blueFacm)};
  background: ${({ selected }) => (selected ? theme.colors.blueEval : 'none')};
  border: 1px solid ${theme.colors.blueEval};
  width: 300px;
  border-radius: 40px;
`
const Labels = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media (max-width: 1000px) {
    flex-direction: column;
    justify-content: space-between;
  }
`
